// Packages and components
import React, { useCallback, useEffect, useState } from 'react';
import { Box } from 'grommet';
import SHDButton from '@ui/Button/SHDButton';
import SHDCloseButton from '@ui/CloseButton/SHDCloseButton';
import SHDFooter from '@ui/Footer/SHDFooter';
import SHDHeading from '@ui/Heading/SHDHeading';
import SHDInput from '@ui/Input/SHDInput';
import SHDInputPhoneNumber from '@ui/InputPhoneNumber/SHDInputPhoneNumber';
import SHDRule from '@ui/Rule/SHDRule';
import SHDText from '@ui/Text/SHDText';
import SHDBackButton from '@ui/BackButton/SHDBackButton';
import SHDModalContainer from '@ui/ModalContainer/SHDModalContainer';

// Helpers and utilities
import { isValidPhoneNumber } from 'react-phone-number-input';
import { useTeamStaff } from '@hooks/useTeam';

// Types and enums
import type {
  InviteTeamManagersPageProps,
  EditRosterFieldV2,
  InviteManager,
} from '../Onboarding.d';
import type { ErrorMessage, InviteBody } from '@theme/theme.d';
import { StaffRole, StaffLimit } from '@theme/theme.d';
import { analytics } from '@services/analytics';

const InviteTeamManagersPage: React.FC<InviteTeamManagersPageProps> = ({
  onContinue,
  onBack,
  teamId,
  userId,
  sportType,
  modalHeight,
}) => {
  // Constants
  const error: ErrorMessage = { message: 'Invalid phone number.' };
  const managerLimit = StaffLimit.Admin - 1;

  // State
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [managerKey, setManagerKey] = useState<number>(0);
  const [managers, setManagers] = useState<InviteManager[]>([
    {
      id: 0,
      hasPhoneError: false,
      name: '',
      invitePhoneNumberList: [],
    },
  ]);

  // Hooks
  const { sendInvite } = useTeamStaff();

  // Methods
  const createBlankRow = useCallback(() => {
    const newKey = managerKey + 1;
    setManagerKey(newKey);
    return {
      id: newKey,
      hasPhoneError: false,
      name: '',
      invitePhoneNumberList: [],
    };
  }, [managerKey]);

  const checkForBlankRows = useCallback(
    (newRows: InviteManager[]) => {
      const blankRows = newRows.filter((row) => {
        return !row.name && !row.invitePhoneNumberList.length;
      });
      if (!blankRows.length && managers.length < managerLimit) {
        newRows.push(createBlankRow());
      }
      return newRows;
    },
    [createBlankRow, managers, managerLimit]
  );

  // Handlers
  const handleInviteManagers = () => {
    setIsSaving(true);
    if (
      managers.length === managerLimit &&
      managers[managers.length - 1].name === '' &&
      managers[managers.length - 1].invitePhoneNumberList.length === 0
    ) {
      managers.pop();
    }

    managers.forEach(async (manager) => {
      const inviteBody: InviteBody = {
        invitedUserPhone: manager.invitePhoneNumberList[0],
        invitedUserName: manager.name,
        teamId: teamId,
        teamRole: StaffRole.Manager,
      };
      analytics.track('Staff Invited', {
        userId: userId,
        teamId: teamId,
        invitedPhoneNumber: manager.invitePhoneNumberList[0],
        sport: sportType,
      });
      await sendInvite(inviteBody);
    });
    onContinue();
  };

  const handleButtonDisabled = () => {
    let isDisabled: boolean = false;

    if (isSaving) {
      isDisabled = true;
    }

    managers.some((manager) => {
      if (managers.length === managerLimit) {
      }

      if (
        managers.length === 1 &&
        manager.name === '' &&
        manager.invitePhoneNumberList.length === 0
      ) {
        isDisabled = true;
      }

      if (
        manager.name !== '' &&
        (manager.invitePhoneNumberList.length === 0 ||
          manager.invitePhoneNumberList[0] === '')
      ) {
        isDisabled = true;
      }

      if (manager.hasPhoneError) {
        isDisabled = true;
      }
    });

    return isDisabled;
  };

  const handleVerifyPhoneNumber = (rowId: number) => {
    const newRows = [...managers];
    const managerIndex = newRows.findIndex((manager) => manager.id === rowId);
    const manager = newRows[managerIndex];

    if (manager.invitePhoneNumberList?.[0]) {
      if (!isValidPhoneNumber(manager.invitePhoneNumberList[0])) {
        manager.hasPhoneError = true;
      } else {
        manager.hasPhoneError = false;
      }
    } else {
      manager.hasPhoneError = false;
    }

    setManagers(newRows);
  };

  const handleRowUpdate = (
    updatedValue: string,
    rowId: number,
    field: EditRosterFieldV2
  ) => {
    let newRows = [...managers];
    const updatedRow = newRows[newRows.findIndex((row) => row.id === rowId)];

    switch (field) {
      case 'name':
        updatedRow.name = updatedValue;
        break;
      case 'invitePhoneNumberList':
        if (!updatedRow.invitePhoneNumberList) {
          updatedRow.invitePhoneNumberList = [];
        }
        updatedRow.invitePhoneNumberList[0] = updatedValue;
        break;
      default:
        break;
    }

    newRows = checkForBlankRows(newRows);
    setManagers(newRows);
  };

  const handleRowRemoval = (rowIndex: number) => {
    const newRows = [...managers];
    let updatedRows = newRows.filter((row) => row.id !== rowIndex);

    updatedRows = checkForBlankRows(updatedRows);
    setManagers(updatedRows);
  };

  // Effects
  useEffect(() => {
    document.querySelectorAll('.manager-row').forEach((row, index) => {
      const closeButton = row.querySelector('.manager-row__close-button');

      if (closeButton) {
        if (index === managers.length - 1) {
          (closeButton as HTMLElement).style.display = 'none';
        } else {
          (closeButton as HTMLElement).style.display = 'block';
        }
      }
    });
  }, [managers]);

  // Renders
  const renderRow = (row: InviteManager) => {
    return (
      <Box
        key={`manager-${row.id}`}
        className="manager-row"
        id={`manager__${row.id}`}
        margin={{ bottom: 'xsmall' }}
      >
        <Box
          direction="row"
          gap="none"
          pad={{ horizontal: 'xxsmall', top: 'xxsmall' }}
        >
          <SHDInput
            isBorderless
            label="Name"
            value={row.name}
            onChange={(newValue) => {
              handleRowUpdate(newValue, row.id, 'name');
            }}
          />
        </Box>

        <Box direction="row" gap="large" align="center" height="20px">
          <Box width="87%">
            <SHDRule />
          </Box>
          <Box className="manager-row__close-button">
            <SHDCloseButton
              isFilled
              background="black-alpha-50"
              size="xsmall"
              onClick={() => {
                handleRowRemoval(row.id);
              }}
            />
          </Box>
        </Box>

        <Box
          direction="row"
          gap="none"
          pad={{ horizontal: 'xxsmall', bottom: 'xxsmall' }}
          border={{ side: 'bottom', color: 'black-alpha-10' }}
        >
          <SHDInputPhoneNumber
            label="Phone number (Required)"
            isBorderless={true}
            phoneNumber={row.invitePhoneNumberList[0]}
            onChange={(newValue) => {
              handleRowUpdate(newValue, row.id, 'invitePhoneNumberList');
            }}
            error={
              managers.find((item) => item.id === row.id)?.hasPhoneError ===
              true
                ? error
                : null
            }
            onFocusOut={() => {
              handleVerifyPhoneNumber(row.id);
            }}
          />
        </Box>
      </Box>
    );
  };

  return (
    <>
      <Box
        style={{
          left: '0',
          position: 'absolute',
          top: '0',
          transform: 'translate(20px, -27px)',
          zIndex: 999,
        }}
      >
        <SHDBackButton onClick={() => onBack()} />
      </Box>

      <Box height={modalHeight} fill="vertical">
        <SHDModalContainer>
          <Box direction="column" gap="xsmall">
            <SHDHeading level={3}>Invite team managers</SHDHeading>
            <SHDText color="gray-900-alpha-80" style="subhead">
              Team managers have full access to edit, score, and stream for your
              team.
            </SHDText>
          </Box>
        </SHDModalContainer>

        <Box
          className="managers-list"
          border={{ side: 'top', color: 'black-alpha-10' }}
        >
          {managers?.map((manager) => {
            return renderRow(manager);
          })}
        </Box>

        <SHDFooter>
          <SHDModalContainer>
            <SHDButton
              primary
              label={isSaving ? 'Saving...' : 'Next'}
              onClick={() => handleInviteManagers()}
              disabled={handleButtonDisabled()}
            />
            <SHDButton label="Do this later" onClick={onContinue} />
          </SHDModalContainer>
        </SHDFooter>
      </Box>
    </>
  );
};

export default InviteTeamManagersPage;
